import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, ReplaySubject, Subject, defer } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import {
    AppConfigStoreService,
    UnifiedQueryV2Service,
} from 'src/app/shared/services';
import {
    SUPPLIER_1688_REQUEST,
    Supplier1688Row,
    SupplierOverridesDoc,
    SupplierProductChange,
} from './supplier-1688';

const SUPPLIER_OVERRIDES_KEY = 'supplier-1688-overrides';

@Injectable({
    providedIn: 'root',
})
export class Supplier1688Service {
    private unifiedQueryV2Service = inject(UnifiedQueryV2Service);
    private configStore = inject(AppConfigStoreService);
    // One read-then-write at a time, in the order they were asked for: two at
    // once read the same document, and the second write erases the first.
    private writes = new Subject<Observable<unknown>>();

    constructor() {
        this.writes.pipe(concatMap((write) => write)).subscribe();
    }

    getRows(): Observable<Supplier1688Row[]> {
        return this.unifiedQueryV2Service.query<Supplier1688Row>(
            SUPPLIER_1688_REQUEST,
        );
    }

    // Empty until the first save (the lambda answers config: null).
    getOverrides(): Observable<SupplierOverridesDoc> {
        return this.configStore
            .get<Partial<SupplierOverridesDoc>>(SUPPLIER_OVERRIDES_KEY)
            .pipe(
                map((config) => ({
                    products: config?.products ?? {},
                    shops: config?.shops ?? {},
                })),
            );
    }

    // null puts the product back on its matched rows.
    saveProduct(
        pid: number,
        change: SupplierProductChange | null,
        newShop?: { url: string; name: string },
    ): Observable<SupplierOverridesDoc> {
        return this.update((doc) => {
            const products = { ...doc.products };
            if (change) {
                products[pid] = {
                    ...change,
                    updatedAt: new Date().toISOString(),
                };
            } else {
                delete products[pid];
            }
            const shops = newShop
                ? { ...doc.shops, [newShop.url]: newShop.name }
                : doc.shops;
            return { products, shops };
        });
    }

    // An empty name goes back to what the shop's address or company name gives.
    saveShopName(url: string, name: string): Observable<SupplierOverridesDoc> {
        return this.update((doc) => {
            const shops = { ...doc.shops };
            if (name) {
                shops[url] = name;
            } else {
                delete shops[url];
            }
            return { products: doc.products, shops };
        });
    }

    // The store replaces whole documents, so each change starts from a fresh
    // read: what this page loaded a while ago may be missing someone else's
    // edit, and writing that copy back would erase it. A failed write reaches
    // its caller and leaves the queue running for the next one.
    private update(
        change: (doc: SupplierOverridesDoc) => SupplierOverridesDoc,
    ): Observable<SupplierOverridesDoc> {
        const result = new ReplaySubject<SupplierOverridesDoc>(1);
        this.writes.next(
            defer(() => this.getOverrides()).pipe(
                switchMap((doc) => {
                    const next = change(doc);
                    return this.configStore
                        .set(SUPPLIER_OVERRIDES_KEY, next)
                        .pipe(map(() => next));
                }),
                tap(result),
                catchError(() => EMPTY),
            ),
        );
        return result;
    }
}
